import React, { useState } from 'react';
import { supabase, isAuthConfigured } from '../services/supabase';
import { trackEvent } from '../services/analytics';

/**
 * Glassmorphic login / sign-up modal. Google OAuth + email-password via Supabase.
 * Degrades gracefully when Supabase keys aren't set yet.
 *
 * Props: mode ('login' | 'signup'), onClose, onAuthed(user)
 */
const AuthModal = ({ mode = 'login', onClose }) => {
  const [tab, setTab] = useState(mode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const isSignup = tab === 'signup';

  const google = async () => {
    if (!isAuthConfigured) { setErr('Auth isn’t configured yet.'); return; }
    trackEvent('Auth Started', { method: 'google', mode: tab });
    setErr(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) setErr(error.message);
  };

  const emailAuth = async (e) => {
    e.preventDefault();
    if (!isAuthConfigured) { setErr('Auth isn’t configured yet — add your Supabase keys.'); return; }
    setBusy(true); setErr(null); setMsg(null);
    trackEvent('Auth Started', { method: 'email', mode: tab });
    try {
      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        if (data.session) {
          // "Confirm email" is OFF in Supabase → signUp returns a live session
          // and the user is logged in immediately.
          trackEvent('Auth Succeeded', { method: 'email' });
          onClose();
        } else {
          // "Confirm email" is ON → no session until they click the email link.
          setMsg('Check your inbox to confirm your email, then log in.');
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        trackEvent('Auth Succeeded', { method: 'email' });
        onClose();
      }
    } catch (e2) {
      setErr(e2.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-white/15 bg-white/10 backdrop-blur-2xl shadow-2xl p-7 text-slate-100 animate-fade-in">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-slate-300 hover:text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <h2 className="text-2xl font-bold mb-1">{isSignup ? 'Create your account' : 'Welcome back'}</h2>
        <p className="text-sm text-slate-300 mb-5">{isSignup ? 'Save searches and get better picks over time.' : 'Log in to pick up where you left off.'}</p>

        <button
          onClick={google}
          className="w-full inline-flex items-center justify-center gap-3 rounded-xl bg-white text-slate-800 font-medium py-2.5 hover:bg-slate-100 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5 text-xs text-slate-400">
          <div className="h-px flex-1 bg-white/15" /> or <div className="h-px flex-1 bg-white/15" />
        </div>

        <form onSubmit={emailAuth} className="space-y-3">
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-2.5 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <input
            type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-2.5 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <button
            type="submit" disabled={busy}
            className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-60 font-semibold py-2.5 transition-colors"
          >
            {busy ? 'Please wait…' : isSignup ? 'Sign up' : 'Log in'}
          </button>
        </form>

        {err && <p className="mt-3 text-sm text-red-300">{err}</p>}
        {msg && <p className="mt-3 text-sm text-emerald-300">{msg}</p>}

        <p className="mt-5 text-center text-sm text-slate-300">
          {isSignup ? 'Already have an account?' : 'New here?'}{' '}
          <button onClick={() => { setErr(null); setMsg(null); setTab(isSignup ? 'login' : 'signup'); }} className="text-violet-300 hover:text-violet-200 font-medium">
            {isSignup ? 'Log in' : 'Create one'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
